// A DECISÃO VIAJA COM O VISITANTE. Guardada no navegador dele, aplicada na visita seguinte a 0 ms.
//
// Sem DOM, sem `window`, sem `localStorage`. O armazenamento é INJETÁVEL, do mesmo jeito que o
// `fetch` é injetável no `resolve.js` — trocar de runtime é trocar o adaptador, não este arquivo.
// O adaptador de navegador mora no `transport.js`, junto do que já existia (`browserValueStorage`).
//
// ## O problema, medido em 21/08/2026 no site de um tenant
//
//     a janela entre o script poder rodar e a página pintar ....  86 ms
//     uma ida até a região .................................... ~590 ms
//
// Sete vezes. Três transportes testados — WebSocket (872 ms), HTTP sem preconnect, HTTP com
// preconnect (593 ms) — e o melhor não chega perto. Decompondo a chamada: 401 dos 419 ms são a perna
// da BORDA até a região. O que falta é DISTÂNCIA, não protocolo, e distância não se otimiza.
//
// Guardar a decisão na borda também não resolve: os 11 tenants juntos têm 14.230 perfis, 611 KB, 44
// bytes por perfil — um KeyValueStore de 5 MB teria teto de 119.243 perfis para a plataforma
// INTEIRA. Um e-commerce médio (200.000 clientes) já é 1,7x esse teto sozinho; uma telecom, 42x.
// Armazém por usuário na borda tem teto, e o produto é vendido para quem ainda vai entrar.
//
// ## A saída: a decisão não é buscada, ela já está lá
//
//     visita N     lê o guardado -> aplica em ~0 ms -> pinta
//                  ... página pronta ...
//                  pergunta à plataforma em segundo plano -> guarda para a próxima
//
//     visita N+1   lê o guardado -> aplica em ~0 ms
//
// Os ~590 ms continuam existindo. Eles só saem do caminho da pintura, porque a decisão passa a ser
// tomada uma visita ANTES. Não é otimização de rede: é mudança de QUANDO se pergunta.
//
// Armazenamento por usuário na plataforma: ZERO. A decisão mora no aparelho do dono dela, e por isso
// não há teto — cada visitante traz o próprio espaço.
//
// ## O que autoriza guardar
//
// Uma decisão guardada é uma AFIRMAÇÃO SOBRE O PRESENTE FEITA NO PASSADO, que é exatamente o que a
// premissa indiscutível deste produto proíbe fazer sem verificação. O que a torna legítima é o
// `validity` que o `/decide` devolve — e ele só existe quando a POLÍTICA DO TENANT declara prazo.
//
//     sem `validity` .... nada é guardado, e tudo segue como sempre foi
//     com `validity` .... guarda-se, e o slot para de valer em `expires_ts`
//
// Não há padrão herdado: viajar é opt-in por política.
//
// ## Por que o CONTEÚDO vem junto, e não só o nome da variante
//
// O desenho separava "a decisão" (por pessoa) de "o conteúdo da variante" (que não é por pessoa e
// cacheia em qualquer lugar). A separação continua verdadeira e é o que impede armazém por usuário
// em cache compartilhado — mas ela não implica DOIS caches aqui dentro. No navegador do dono, o
// conteúdo não é compartilhado com ninguém, e buscá-lo à parte poria uma ida de rede de volta no
// caminho da pintura, que é o problema inteiro que este módulo existe para resolver.
//
// Guardando o conteúdo junto, o que fica no armazém é exatamente a última resposta do
// `resolveSlots` — mesma forma, mesmos campos. É isso que faz `aplicarSlots` servir aos dois
// caminhos sem saber de qual veio, e uma forma só é o que impede os dois divergirem.
//
// ## E o DADO DO SUJEITO vai junto, sob o MESMO prazo
//
// Quando o conteúdo cita `{{chave}}`, o valor viaja no mesmo blob e é guardado com ele. Isso é uma
// afirmação sobre o presente feita no passado, exatamente como a decisão, e o que a autoriza é
// exatamente o mesmo: o `validity` que a política do tenant declarou. Vencido o prazo, os dois
// somem juntos, porque são o mesmo registro.
//
// A alternativa era guardar a decisão e não o valor. Ela foi recusada: na visita seguinte o conteúdo
// que cita chave seria recusado por falta de dado (o SDK não entrega frase com lacuna), e o mesmo
// visitante veria o banner numa visita e não na outra, sem que nada tivesse mudado. Um caminho
// rápido que discorda do caminho de rede é pior que o atraso que ele evita.
//
// ## O que NÃO mora aqui: assinatura
//
// O blob não é verificado por este módulo, e não teria como ser — o segredo do tenant não pode estar
// no navegador. Quem tem o aparelho pode editar o próprio blob e se dar outra variante. Para um
// banner, isso custa ao visitante ver outro texto.
//
// Um blob dizendo "esta pessoa tem 20% de desconto" seria outra coisa, e aí a regra do produto já é
// conhecida: o SYNQi3 entrega a um RECEPTOR, nunca ao destinatário final, e é o receptor quem
// confere antes de conceder. Concretamente, quando houver variante que concede valor: o `validity`
// ganha HMAC do tenant, o blob volta ao receptor junto do pedido, e o receptor valida contra o
// segredo que só ele e a plataforma têm. Nada disso muda este arquivo — muda quem lê o blob do
// outro lado.

import { aplicarSlots, resolverComDesfecho } from "./resolve.js";

/** Versão do formato guardado. Guardado de outra versão é descartado, nunca adivinhado. */
export const FORMATO_DECISAO = 1;

/** Chave sugerida. Fica ao lado do `synqi3_pending` da fila e do `synqi3_anonymous_id`. */
export const CHAVE_DECISAO = "synqi3_decisao";

/**
 * Prazo da RENOVAÇÃO, que não é o prazo do render.
 *
 * O `PRAZO_PADRAO_MS` do `resolve.js` é 1 s porque lá o custo do atraso é FLICKER: passar do prazo
 * é pior que não decidir. Aqui não há nada disso — a renovação roda depois de a página já estar
 * pintada, e o custo de ela demorar é zero. O custo de ela ser ABORTADA, sim, é real: a próxima
 * visita fica sem decisão e volta ao caminho lento.
 *
 * Herdar 1 s aqui era aplicar o prazo do caminho quente a uma chamada de segundo plano. Medido em
 * 22/08/2026 desta máquina (Brasil), a chamada custa 400-638 ms — perto demais de 1 s para uma
 * conexão fria de celular, onde DNS + TCP + TLS somam segundos antes do primeiro byte.
 *
 * 10 s é ~15x o custo medido: cobre um aperto de mão ruim e ainda impede a promessa de ficar
 * pendurada para sempre. Quem sair da página leva a requisição junto, de qualquer jeito.
 */
export const PRAZO_RENOVACAO_MS = 10000;

/**
 * O que ainda VALE do que está guardado, na mesma forma que `resolveSlots` devolve.
 *
 * Mesma forma de propósito: quem renderiza não deve saber se a decisão veio da rede agora ou do
 * armazém de uma visita atrás. `{}` significa o mesmo dos dois lados — siga com o que a página já
 * tinha.
 *
 * @param {object|null} guardado  o blob lido do armazém (já em objeto)
 * @param {{tenant: string, userId: string, agora?: number}} o
 * @returns {Record<string, object>}
 */
export function decisaoGuardada(guardado, { tenant, userId, agora = Date.now() } = {}) {
  const b = guardado;
  if (!b || typeof b !== "object") return {};
  // Formato diferente é dado que não sabemos ler. Tentar aproveitar campos conhecidos seria
  // adivinhar o resto.
  if (b.v !== FORMATO_DECISAO) return {};
  // DE QUEM É. Sem isto, quem fizesse login depois de outra pessoa no mesmo aparelho receberia a
  // decisão dela — o pior defeito possível aqui, e o mesmo que o CloudFront cometeu com o
  // `session_id` do PDV de um tenant.
  if (!tenant || !userId || b.t !== tenant || b.u !== userId) return {};

  const fora = {};
  for (const [slot, dado] of Object.entries(b.s || {})) {
    if (_vale(dado, agora)) fora[slot] = dado;
  }
  return fora;
}

/**
 * O que GUARDAR a partir do que a plataforma acabou de responder. `null` = não guarde nada.
 *
 * `null` não é "mantenha o que tinha": é APAGUE. Quem chama distingue as duas coisas pelo desfecho
 * do `resolverComDesfecho` — se a plataforma não respondeu, nem se chama esta função.
 */
export function paraGuardar(slots, { tenant, userId, agora = Date.now(), anterior = null } = {}) {
  if (!tenant || !userId) return null;
  const s = {};
  for (const [slot, dado] of Object.entries(slots || {})) {
    // Só viaja o que a POLÍTICA autorizou a viajar. Slot sem `validity` é decisão de uso único.
    if (_prazo(dado) > 0) s[slot] = _comOrcamentoPreservado(dado, anterior, slot, agora);
  }
  if (!Object.keys(s).length) return null;
  return { v: FORMATO_DECISAO, t: tenant, u: userId, d: agora, s };
}

/**
 * O orçamento NUNCA SOBE numa renovação. MEDIDO em 22/08/2026 e este é o defeito que fecha:
 *
 *     aplicou 1x -> desconta local (3 -> 2) -> renova em segundo plano -> volta a 3
 *
 * O servidor não estava errado: ele conta `imp_<variante>_n`, que só existe depois de o evento
 * `personalization_shown` atravessar ingest -> Kinesis -> signal_processor -> cache. Isso leva
 * segundos a minutos, e a renovação acontece logo depois de pintar. Ou seja, TODA renovação
 * devolvia o orçamento antes de o servidor saber da impressão — e um teto de 3 virava dez
 * exibições numa visita de dez páginas.
 *
 * A regra: enquanto a entrada local está VIVA, ela é a mais pessimista das duas e vale o `min`.
 * Depois de vencida, o servidor manda sozinho — nesse ponto ele já teve o prazo inteiro (110 min no
 * tenant medido) para receber as impressões, muito mais que a defasagem do pipeline.
 *
 * Variante DIFERENTE recomeça: o teto é por variante, e o gasto de uma não é o gasto da outra.
 */
function _comOrcamentoPreservado(dado, anterior, slot, agora) {
  const velho = anterior && anterior.s && anterior.s[slot];
  if (!velho || velho.variant !== dado.variant) return dado;
  if (_prazo(velho) <= agora) return dado;              // vencido: o servidor já teve tempo de saber
  const nLocal = _orcamento(velho);
  const nNovo = _orcamento(dado);
  if (nLocal === null || nNovo === null || nLocal >= nNovo) return dado;
  return { ...dado, validity: { ...dado.validity, applies_left: nLocal } };
}

/**
 * Desconta do orçamento os slots que foram aplicados. `null` = não sobrou nada, apague.
 *
 * O freq cap é conferido pelo `/decide` na HORA DE DECIDIR, e uma decisão que viaja não é
 * redecidida. Sem descontar aqui, o mesmo blob aplicaria a mesma variante em toda visita dentro do
 * prazo, e um teto de três seria furado em silêncio: o `signal_processor` contaria as impressões e
 * o cap só perceberia na próxima ida à plataforma. O orçamento vem em `validity.applies_left`.
 *
 * Slot sem `applies_left` é slot cuja política não declarou teto: não há o que descontar.
 *
 * @param {object|null} guardado
 * @param {Array<{slot: string}>} aplicados  o retorno de `aplicarSlots`
 */
export function descontarAplicacoes(guardado, aplicados) {
  if (!guardado || typeof guardado !== "object" || !guardado.s) return null;
  const gastos = new Set((aplicados || []).map((a) => a && a.slot).filter(Boolean));
  const s = {};
  for (const [slot, dado] of Object.entries(guardado.s)) {
    if (!gastos.has(slot)) { s[slot] = dado; continue; }
    const n = _orcamento(dado);
    if (n === null) { s[slot] = dado; continue; }         // sem teto declarado: nada a descontar
    // Orçamento zerado FICA, em vez de sumir. Apagar a entrada apagaria junto a memória de que ela
    // foi gasta — e a renovação seguinte, vindo do servidor com o teto cheio (porque a impressão
    // ainda não atravessou o pipeline), devolveria as três exibições. O `decisaoGuardada` já se
    // recusa a aplicar quem está em zero; a entrada só desaparece quando VENCE.
    s[slot] = { ...dado, validity: { ...dado.validity, applies_left: Math.max(0, n - 1) } };
  }
  if (!Object.keys(s).length) return null;
  return { ...guardado, s };
}

// ── os dois momentos, para quem não quiser montar a sequência na mão ─────────────────────────────
//
// A ordem é o que faz a coisa funcionar, e é fácil de errar: aplicar DEPOIS de perguntar recria o
// flicker que este módulo existe para matar. Por isso as duas funções abaixo, uma por momento.

/**
 * ANTES DE PINTAR. Síncrona e sem rede: lê o guardado, aplica, e desconta o que foi aplicado.
 *
 * @param {object} o
 * @param {{read: () => any, write: (v: any) => void}} o.storage
 * @param {(slot: string, texto: string, dado: object) => boolean} o.aplicar  como em `aplicarSlots`
 * @returns {Array<{slot: string, variant: string}>}  o que foi aplicado — só isso vira impressão
 */
export function aplicarGuardado({ storage, tenant, userId, agora = Date.now(), aplicar } = {}) {
  if (!storage || !aplicar) return [];
  let guardado = null;
  try {
    guardado = storage.read();
  } catch {
    return [];                          // armazém indisponível (modo privado, cota): siga sem
  }
  const validos = decisaoGuardada(guardado, { tenant, userId, agora });
  if (!Object.keys(validos).length) return [];
  const feitos = aplicarSlots(validos, aplicar);
  if (feitos.length) _gravar(storage, descontarAplicacoes(guardado, feitos));
  return feitos;
}

/**
 * DEPOIS DE PINTAR. Pergunta à plataforma e guarda para a PRÓXIMA visita.
 *
 * O custo da ida continua o mesmo; ele só deixa de estar no caminho da pintura. Quem chama não
 * deve esperar por isto para renderizar.
 *
 * Três desfechos, e os três importam:
 *
 *     não respondeu ............. mantém o guardado (um piscar de wifi não apaga decisão válida)
 *     respondeu com decisão ..... substitui
 *     respondeu com NADA ........ APAGA — é assim que um opt-out chega ao navegador
 */
export async function renovarGuardado({ storage, tenant, userId, agora = Date.now(),
                                        timeoutMs = PRAZO_RENOVACAO_MS, ...opcoes } = {}) {
  if (!storage) return { respondeu: false, slots: {} };
  const r = await resolverComDesfecho({ userId, timeoutMs, ...opcoes });
  if (!r.respondeu) return r;
  let anterior = null;
  try {
    anterior = storage.read();                // o que já foi gasto não pode ser devolvido
  } catch { /* armazém indisponível: segue sem o piso, que é o comportamento antigo */ }
  _gravar(storage, paraGuardar(r.slots, { tenant, userId, agora, anterior }));
  return r;
}

// ── internos ─────────────────────────────────────────────────────────────────────────────────────
function _gravar(storage, valor) {
  try {
    storage.write(valor);               // `null` apaga: é o contrato do `browserValueStorage`
  } catch {
    /* cota estourada ou armazém bloqueado: não guardar é degradação, não erro */
  }
}

function _prazo(dado) {
  const v = dado && dado.validity;
  const e = v && v.expires_ts;
  return typeof e === "number" && isFinite(e) ? e : 0;
}

function _orcamento(dado) {
  const n = dado && dado.validity && dado.validity.applies_left;
  return typeof n === "number" && isFinite(n) ? n : null;
}

function _vale(dado, agora) {
  const e = _prazo(dado);
  if (!e || e <= agora) return false;   // vencido, ou nunca teve prazo: não é decisão vigente
  const n = _orcamento(dado);
  return n === null || n > 0;
}
